import { Archive, FolderKanban, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  createIntentKey,
  useCreateProjectMutation,
  useProjectCardsQuery,
  useUpdateProjectMutation,
  type Project,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldError,
  FieldLabel,
  Input,
  ResourceActionsMenu,
  StateSurface,
  Textarea,
} from '../../design-system/index.js';
import { PageHeader, formatTime } from '../shared/page.js';
import { ProjectDeleteDialog } from './project-delete-dialog.js';

export function ProjectsPage() {
  const { connection, library } = useWorkspace();
  const projects = useProjectCardsQuery({
    connection_id: connection.id,
    library_id: library.id,
    limit: 100,
  });
  const update = useUpdateProjectMutation();
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const items = [...(projects.data?.items ?? [])].sort((left, right) => {
    if (left.status !== right.status) return left.status === 'active' ? -1 : 1;
    return right.updated_at.localeCompare(left.updated_at);
  });

  return (
    <div>
      <PageHeader
        title="Projects"
        action={<CreateProjectDialog open={creating} onOpenChange={setCreating} />}
      />
      {projects.isLoading ? (
        <StateSurface kind="loading" title="Loading projects" />
      ) : projects.error ? (
        <StateSurface
          kind="error"
          title="Projects unavailable"
          action={
            <Button variant="secondary" onClick={() => void projects.refetch()}>
              Try again
            </Button>
          }
        />
      ) : !items.length ? (
        <StateSurface title="No projects available" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((project) => {
            const Icon = project.status === 'archived' ? Archive : FolderKanban;
            return (
              <article
                key={project.id}
                className="group flex min-h-44 flex-col rounded-lg border border-border bg-surface p-5 transition-colors hover:border-accent-line hover:bg-surface-hi"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="grid size-10 place-items-center rounded-md border border-accent-line bg-accent-soft text-accent">
                    <Icon aria-hidden="true" className="size-4" />
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="rounded-full border border-border px-2 py-1 font-mono text-[0.65rem] uppercase tracking-wide text-ink-3">
                      {project.status}
                    </span>
                    <ResourceActionsMenu
                      label={`Actions for ${project.title}`}
                      actions={[
                        {
                          label:
                            project.status === 'active' ? 'Archive project' : 'Restore project',
                          icon:
                            project.status === 'active' ? (
                              <Archive aria-hidden="true" />
                            ) : (
                              <RotateCcw aria-hidden="true" />
                            ),
                          disabled: update.isPending,
                          onSelect: () =>
                            void update
                              .mutateAsync({
                                projectId: project.id,
                                body: {
                                  title: project.title,
                                  brief: project.brief,
                                  notes: project.notes,
                                  status: project.status === 'active' ? 'archived' : 'active',
                                  expected_revision: project.revision,
                                },
                              })
                              .catch(() => undefined),
                        },
                        {
                          label: 'Delete project',
                          icon: <Trash2 aria-hidden="true" />,
                          destructive: true,
                          onSelect: () => setDeleteTarget(project),
                        },
                      ]}
                    />
                  </div>
                </div>
                <Link
                  to={`/projects/${project.id}`}
                  className="-mx-2 mt-3 flex flex-1 flex-col rounded-sm px-2 outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
                >
                  <h2 className="truncate font-semibold text-ink group-hover:text-accent">
                    {project.title}
                  </h2>
                  {project.brief ? (
                    <p className="mt-1 line-clamp-2 text-sm text-ink-3">{project.brief}</p>
                  ) : null}
                  <div className="mt-auto pt-4">
                    <dl className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-ink-2">
                      <div className="flex gap-1">
                        <dt className="sr-only">Songs</dt>
                        <dd>{formatProjectCount(project.song_count, 'song')}</dd>
                      </div>
                      <div className="flex gap-1">
                        <dt className="sr-only">Downloaded</dt>
                        <dd>{project.downloaded_song_count} downloaded</dd>
                      </div>
                    </dl>
                    <p className="mt-2 font-mono text-xs text-ink-3">
                      Updated {formatTime(project.updated_at)}
                    </p>
                  </div>
                </Link>
              </article>
            );
          })}
        </div>
      )}
      {update.error ? (
        <p className="mt-4 text-sm text-err" role="alert">
          The project could not be updated. Refresh and try again.
        </p>
      ) : null}
      <ProjectDeleteDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        project={deleteTarget}
      />
    </div>
  );
}

function formatProjectCount(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function CreateProjectDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateProjectMutation();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [notes, setNotes] = useState('');
  const pendingIntent = useRef<{
    fingerprint: string;
    idempotencyKey: string;
  } | null>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus />
          New project
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Create project</DialogTitle>
        <form
          className="grid gap-4"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!title.trim()) return;
            const body = {
              title: title.trim(),
              brief: brief.trim() || null,
              notes: notes.trim() || null,
            };
            const fingerprint = JSON.stringify(body);
            const idempotencyKey =
              pendingIntent.current?.fingerprint === fingerprint
                ? pendingIntent.current.idempotencyKey
                : createIntentKey();
            pendingIntent.current = { fingerprint, idempotencyKey };
            try {
              const project = await create.mutateAsync({ idempotencyKey, body });
              pendingIntent.current = null;
              onOpenChange(false);
              navigate(`/projects/${project.id}`);
            } catch {
              // Mutation state renders the error; retaining the key makes an exact retry safe.
            }
          }}
        >
          <Field>
            <FieldLabel htmlFor="project-title">Title</FieldLabel>
            <Input
              id="project-title"
              autoFocus
              maxLength={160}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="project-brief">Creative brief</FieldLabel>
            <Textarea
              id="project-brief"
              rows={4}
              maxLength={10_000}
              value={brief}
              onChange={(event) => setBrief(event.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="project-notes">Notes</FieldLabel>
            <Textarea
              id="project-notes"
              rows={5}
              maxLength={100_000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Field>
          {create.error ? <FieldError>The project could not be created.</FieldError> : null}
          <div className="flex justify-end">
            <Button type="submit" disabled={!title.trim() || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create project'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
